import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import BayarForm from "@/components/BayarForm";
import { useAuth } from "@/context/AuthContext";
import {
  apiGetJersey,
  apiGetPengaturan,
  apiJerseyChecklist, apiJerseyChecklistSemua,
  apiJerseyDuplikat,
  apiJerseySiapAcc,
  apiJerseyAcc,
  apiJerseyBukaKunci,
  formatApiErrorDetail,
} from "@/lib/api";
import { qty, rupiah, spkTunggu, tanggalIndo } from "@/lib/format";
import { barisDuplikat } from "@/lib/jersey";
import { Loader2, Lock, Unlock, Pencil, Printer, ArrowLeft, ShieldCheck, Users } from "lucide-react";
import { useCetak } from "@/hooks/usePengaturan";
import LinkPublik from "@/components/LinkPublik";
import AccPanel from "@/components/AccPanel";
import WaStatus from "@/components/WaStatus";
import KirimLink from "@/components/KirimLink";
import * as W from "@/lib/waktu";
import FileMaster from "@/components/FileMaster";
import { bolehLihatFileMaster } from "@/lib/fileMaster";

const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const boxCls = "rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-3";
const h2Cls = "text-[13px] font-semibold text-slate-200";
export const STATUS_JERSEY = {
  ORDER: ["Order", "border-slate-600 bg-slate-800/50 text-slate-300"],
  SIAP_ACC: ["Siap ACC customer", "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"],
  ACC: ["ACC customer · terkunci", "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"],
};
const BAGIAN = { produk: "Produk", nama: "Nama & nomor", ukuran: "Ukuran" };
const AKSI = { SIMPAN: "Disimpan", SIAP_ACC: "Siap ACC", ACC: "ACC customer", BUKA_KUNCI: "Kunci dibuka", UBAH: "Diubah" };
const waktu = (iso) => (iso ? W.waktu(iso) : "—");

export default function JerseyDetail() {
  const cetak = useCetak();
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();
  const [d, setD] = React.useState(null);
  const [cfg, setCfg] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(location.state?.notice || null);  // v2.23: after "Lengkapi nama & nomor"
  const [alasanBuka, setAlasanBuka] = React.useState("");
  const [alasanDup, setAlasanDup] = React.useState("");
  const stokInfo = location.state?.stok || [];
  const perubahan = location.state?.perubahan;

  const load = React.useCallback(async () => {
    try { setD(await apiGetJersey(id)); } catch (e) { setError(errText(e)); }
  }, [id]);
  React.useEffect(() => { load(); apiGetPengaturan().then(setCfg).catch(() => {}); }, [load]);

  const act = async (key, fn, msg) => {
    setBusy(key); setError(null); setNotice(null);
    try { setD(await fn()); if (msg) setNotice(msg); } catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };

  if (!d) return <div className="min-h-screen bg-slate-950 text-slate-200"><TopBar /><main className="max-w-6xl mx-auto px-4 py-8">{error ? <div className="text-red-300">{error}</div> : <Loader2 className="animate-spin" />}</main></div>;

  const o = d.order, j = d.jersey, nota = d.nota, spk = d.spk;
  const batal = o.status_order === "CANCELLED";
  const terkunci = d.status_jersey === "ACC";
  const [stLabel, stCls] = STATUS_JERSEY[d.status_jersey] || [d.status_jersey, ""];
  const dup = barisDuplikat(j.pemain);
  const ringkas = nota ? { total: nota.total_snapshot, dibayar: nota.total_dibayar || 0, sisa: nota.sisa ?? nota.total_snapshot } : null;

  return (
    <div data-testid="jersey-detail-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
        <Link to="/jersey" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200"><ArrowLeft size={14} /> Order Masuk Jersey</Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">
              Order Jersey <span data-testid="jersey-faktur" className="font-mono text-amber-300">{o.nomor_faktur}</span>
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              {batal ? <span data-testid="jersey-status" className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-xs font-mono text-red-300">DIBATALKAN</span>
                : <span data-testid="jersey-status" className={`rounded-md border px-2 py-0.5 text-xs font-mono ${stCls}`}>{terkunci && <Lock size={11} className="inline mr-1" />}{stLabel}</span>}
              {d.revisi_ke > 0 && <span data-testid="jersey-revisi" className="rounded-md border border-amber-500/40 px-2 py-0.5 text-xs font-mono text-amber-300">Revisi {d.revisi_ke}</span>}
              {/* v2.21: one nota split per design = several SPKs, each through production on its own */}
              {(d.spks || []).length > 1 ? (
                <span data-testid="jersey-spk-daftar" className="basis-full text-slate-400">{d.spks.length} SPK (satu per desain):{" "}
                  {d.spks.map((x, i) => (
                    <span key={x.id} data-testid={`jersey-spk-${i + 1}`} className="mr-3 inline-block">Desain {i + 1} <span className="font-mono text-slate-200">{x.nomor_spk}</span> · {x.status_spk === "READY" ? "di antrean produksi" : spkTunggu(x) || x.status_spk}
                      {cetak.spk && <a href={`/spk/${x.id}/cetak`} target="_blank" rel="noopener noreferrer" className="ml-1 text-cyan-300"><Printer size={11} className="inline" /></a>}</span>))}
                </span>
              ) : spk && <span className="text-slate-400">SPK <span className="font-mono text-slate-200">{spk.nomor_spk}</span> · <span data-testid="jersey-spk-status">{spk.status_spk === "READY" ? "di antrean produksi" : spk.status_spk === "CANCELLED" ? "dibatalkan" : spkTunggu(spk) || spk.status_spk}</span></span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!batal && !terkunci && <button data-testid="jersey-ubah" onClick={() => navigate(`/jersey/${id}/ubah`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/50"><Pencil size={14} /> Ubah data</button>}
            {/* v2.23: names after the customer's ACC — no revision, no price change, the approval stays */}
            {!batal && !terkunci && <button data-testid="jersey-lengkapi-nama" onClick={() => navigate(`/jersey/${id}/nama`)} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/60 px-3 py-2 text-sm text-slate-100 hover:bg-amber-500/10"><Users size={14} /> Lengkapi nama &amp; nomor</button>}
            {nota && <LinkPublik kind="nota" id={nota.id} label="Salin link nota" testid="jersey-link-nota" />}
            {nota && !batal && <KirimLink notaId={nota.id} testid="jersey-kirim-link" />}
            {nota && cetak.nota && <a data-testid="jersey-cetak-nota" href={`/nota/${nota.id}/cetak`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/50"><Printer size={14} /> Nota</a>}
            {spk && cetak.spk && (d.spks || []).length <= 1 && <a data-testid="jersey-cetak-spk" href={`/spk/${spk.id}/cetak`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/50"><Printer size={14} /> SPK Jersey (A4)</a>}
          </div>
        </div>

        {error && <div data-testid="jersey-detail-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        {notice && <div data-testid="jersey-detail-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {stokInfo.map((w) => <div key={w} className="text-xs text-amber-300">⚠ {w}</div>)}
        {/* v2.22: design master files per SPK (one SPK per design since v2.21) */}
        {bolehLihatFileMaster(user?.role) && ((d.spks || []).length ? d.spks : spk ? [spk] : []).filter((x) => x.status_spk !== "CANCELLED").map((x, i, semua) => (
          <div key={x.id} className="space-y-1">
            {semua.length > 1 && <div className="text-xs text-slate-400">Desain {i + 1} · <span className="font-mono">{x.nomor_spk}</span></div>}
            <FileMaster spkId={x.id} testid={`jersey-fm-${i + 1}`} />
          </div>
        ))}
        {perubahan && (
          <div data-testid="jersey-stok-disesuaikan" className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-4 py-2.5 text-sm text-cyan-200">
            Perubahan tersimpan. Stok disesuaikan: {perubahan.length ? perubahan.map((p) => `${p.kode} ${p.selisih_milli > 0 ? "keluar" : "kembali"} ${qty(Math.abs(p.selisih_milli))} ${p.satuan || ""}`).join(" · ") : "tidak ada perubahan bahan"}
          </div>
        )}

        {/* F1.5-I: mockup + customer ACC (phone page or WhatsApp) before the final ACC */}
        {!batal && !terkunci && (
          <div className={`${boxCls} border-amber-500/30`}>
            <h2 className={h2Cls}>Mockup & ACC customer</h2>
            <AccPanel orderId={id} jenis="JERSEY" acc={d.order?.acc_customer} onChanged={load} testid="jersey-acc-customer" jumlahDesain={d.jumlah_desain || 1} />
          </div>
        )}
        <WaStatus orderId={id} refreshKey={d} testid="jersey-wa-status" />

        {/* ACC panel */}
        {!batal && (
          <div data-testid="jersey-acc-panel" className={`${boxCls} ${terkunci ? "border-emerald-500/30" : "border-cyan-500/30"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className={h2Cls}>Checklist kunci sebelum produksi</h2>
              <div className="flex flex-wrap gap-2">
                {d.status_jersey === "ORDER" && (
                  <button data-testid="jersey-siap-acc" disabled={!!busy} onClick={() => act("siap", () => apiJerseySiapAcc(id), "Order ditandai Siap ACC customer")}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/50 px-3 py-2 text-sm text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-50">Simpan &amp; lanjut (Siap ACC)</button>
                )}
                {!terkunci && (
                  <button data-testid="jersey-acc" disabled={!!busy} onClick={() => act("acc", () => apiJerseyAcc(id), "ACC customer tercatat. Data terkunci, SPK masuk produksi.")}
                    title={d.boleh_acc ? "" : d.alasan_belum_acc.join("\n")}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold ${d.boleh_acc ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-slate-700 text-slate-300"} disabled:opacity-50`}>
                    <ShieldCheck size={15} /> ACC CUSTOMER</button>
                )}
              </div>
            </div>
            {!terkunci && !d.boleh_acc && <div data-testid="jersey-acc-alasan" className="text-xs text-amber-300">Belum bisa ACC: {d.alasan_belum_acc.join(" · ")}</div>}
            {!terkunci && d.checklist.some((c) => !c.otomatis && !c.ok) && (
              <button data-testid="jersey-centang-semua" disabled={!!busy}
                onClick={() => {
                  const belum = d.checklist.filter((c) => !c.otomatis && !c.ok);
                  if (!window.confirm(`Centang ${belum.length} butir checklist sekaligus?\n\nPastikan semua butir sudah benar-benar dicek bersama data dan mockup yang disetujui customer:\n- ${belum.map((c) => c.label).join("\n- ")}`)) return;
                  act("ck-semua", () => apiJerseyChecklistSemua(id), `${belum.length} butir checklist dicentang`);
                }}
                className="self-start rounded-lg border border-emerald-500/50 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-50">
                Centang semua ({d.checklist.filter((c) => !c.otomatis && !c.ok).length} butir) — sudah dicek</button>
            )}
            {terkunci && <div className="text-sm text-emerald-300"><Lock size={13} className="inline mr-1" /> Data terkunci setelah ACC. Perubahan hanya setelah Owner membuka kunci.</div>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(BAGIAN).map(([b, label]) => (
                <div key={b} className="space-y-1.5">
                  <div className="text-xs font-semibold text-slate-300">{label}</div>
                  {d.checklist.filter((c) => c.bagian === b).map((c) => (
                    <label key={c.kunci} data-testid={`ck-${c.kunci}`} className={`flex items-start gap-2 text-sm ${c.ok ? "text-emerald-200" : "text-slate-300"}`}>
                      <input type="checkbox" checked={c.ok} disabled={c.otomatis || terkunci || !!busy}
                        onChange={(e) => act(`ck-${c.kunci}`, () => apiJerseyChecklist(id, c.kunci, e.target.checked))} className="mt-0.5 accent-emerald-500" />
                      <span>{c.label}{c.otomatis && <span className="ml-1 text-[10px] font-mono text-cyan-300">otomatis</span>}
                        {!c.otomatis && c.ok && c.oleh && <span className="block text-[10px] text-slate-500">{c.oleh.username} · {waktu(c.waktu)}</span>}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            {d.cek.duplikat.length > 0 && !terkunci && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2">
                <div className="text-sm text-red-300">Nama + nomor ganda: {d.cek.duplikat.map((g) => `baris ${g.map((i) => i + 1).join(", ")}`).join("; ")}</div>
                {d.duplikat_disengaja ? <div className="text-xs text-slate-300">Dikonfirmasi disengaja: {d.duplikat_disengaja.alasan} ({d.duplikat_disengaja.oleh?.username})</div> : (
                  <div className="flex gap-2"><input data-testid="jersey-dup-alasan" value={alasanDup} onChange={(e) => setAlasanDup(e.target.value)} placeholder="Alasan jika memang disengaja" className={inputCls} />
                    <button data-testid="jersey-dup-konfirmasi" onClick={() => act("dup", () => apiJerseyDuplikat(id, alasanDup))} className="mt-1 shrink-0 rounded-lg border border-red-500/40 px-3 text-xs text-red-200">Konfirmasi</button></div>
                )}
              </div>
            )}
            {isAdmin && terkunci && (
              <div data-testid="jersey-buka-kunci-panel" className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                <div className="text-sm font-bold text-soq-biru">Owner · buka kunci</div>
                <p className="text-xs text-slate-400">Hanya bisa selama produksi belum melewati {(cfg?.jersey_pos_boleh_buka_kunci || []).join(", ")}. SPK keluar dari antrean, checklist direset, customer wajib ACC ulang.</p>
                <div className="flex gap-2"><input data-testid="jersey-buka-alasan" value={alasanBuka} onChange={(e) => setAlasanBuka(e.target.value)} placeholder="Alasan (wajib)" className={inputCls} />
                  <button data-testid="jersey-buka-kunci" disabled={!!busy} onClick={() => act("buka", () => apiJerseyBukaKunci(id, alasanBuka), "Kunci dibuka. Ubah data lalu minta ACC ulang.").then(() => setAlasanBuka(""))}
                    className="mt-1 shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-sm font-semibold text-slate-950 disabled:opacity-50"><Unlock size={14} /> Buka kunci</button></div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className={boxCls}>
            <h2 className={h2Cls}>Bagian 1 · Identitas</h2>
            <dl className="text-sm grid grid-cols-3 gap-y-1">
              <dt className="text-slate-500">Customer</dt><dd className="col-span-2">{nota?.customer_snapshot?.nama} <span className="text-slate-500">{nota?.customer_snapshot?.nomor_telepon}</span></dd>
              <dt className="text-slate-500">Tim</dt><dd className="col-span-2">{j.tim_komunitas || "—"}</dd>
              <dt className="text-slate-500">PIC</dt><dd className="col-span-2">{j.pic_nama || "—"} <span className="text-slate-500">{j.pic_whatsapp}</span></dd>
              <dt className="text-slate-500">{j.cara_serah === "KIRIM" ? "Kirim" : "Ambil"}</dt><dd className="col-span-2">{tanggalIndo(j.tanggal_ambil_kirim)}</dd>
              <dt className="text-slate-500">Designer</dt><dd className="col-span-2">{o.designer?.nama}</dd>
              <dt className="text-slate-500">Catatan</dt><dd className="col-span-2">{o.catatan || "—"}</dd>
            </dl>
          </div>
          <div className={`${boxCls} lg:col-span-2`}>
            <h2 className={h2Cls}>Bagian 2 · Produk</h2>
            <table className="w-full text-sm"><tbody>
              {o.items.map((it, i) => (
                <tr key={i} className="border-b border-slate-800/60">
                  <td className="py-1.5 pr-2 text-xs text-slate-500">{it.jenis_baris === "VARIASI" ? `V${it.baris_no}` : "+"}</td>
                  <td className="py-1.5"><span className="font-mono text-amber-300">{it.kode_barang}</span> {it.nama_barang}<div className="text-xs text-slate-500">{it.detail_pekerjaan}{it.motif ? ` · motif ${it.motif}` : ""}{it.cutting ? ` · cutting ${it.cutting}` : ""}{it.ukuran_ringkas ? ` · ${it.ukuran_ringkas}` : ""}</div></td>
                  <td className="py-1.5 text-right font-mono">{it.quantity}</td>
                  <td className="py-1.5 text-right font-mono text-slate-400">{rupiah(it.harga)}</td>
                  <td className="py-1.5 text-right font-mono">{rupiah(it.jumlah)}</td>
                </tr>
              ))}
            </tbody></table>
            <div className="flex flex-wrap justify-between text-sm"><span>Total PCS <b>{j.total_pcs}</b></span>
              <span>Subtotal {rupiah(o.subtotal)} · Diskon {rupiah(o.diskon)} · <b data-testid="jersey-total" className="text-amber-300">Total {rupiah(o.total)}</b></span></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className={`${boxCls} lg:col-span-2`}>
            <h2 className={h2Cls}>Bagian 3 · Nama &amp; nomor ({j.pemain.length})</h2>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm" data-testid="jersey-detail-pemain"><thead><tr className="text-left text-xs text-slate-500"><th>#</th><th>Nama</th><th>Nomor</th><th>Ukuran</th><th>Lengan</th><th>Catatan</th></tr></thead>
                <tbody>{j.pemain.map((p, i) => (
                  <tr key={i} className={dup.has(i) ? "bg-red-500/10" : ""}><td className="text-xs text-slate-500">{i + 1}</td><td>{p.nama}</td><td className="font-mono">{p.nomor}</td><td className="font-mono">{p.ukuran}</td><td className="text-xs">{p.lengan}</td><td className="text-xs text-slate-400">{p.catatan}</td></tr>
                ))}</tbody></table>
            </div>
          </div>
          <div className={boxCls}>
            <h2 className={h2Cls}>Bagian 4 · Size &amp; quantity</h2>
            <div className="grid grid-cols-4 gap-2 text-center" data-testid="jersey-detail-ukuran">
              {Object.entries(j.ukuran).map(([u, n]) => (
                <div key={u} className={`rounded border px-1 py-1 ${n ? "border-amber-500/40" : "border-slate-800"}`}><div className="text-[10px] text-slate-500">{u}</div><div className="font-mono">{n}</div></div>
              ))}
            </div>
            {j.ukuran_custom_catatan && <div className="text-xs text-slate-400">Custom: {j.ukuran_custom_catatan}</div>}
            {d.cek.peringatan.map((w) => <div key={w} className="text-xs text-amber-300">⚠ {w}</div>)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {nota && (
            <div className={boxCls}>
              <h2 className={h2Cls}>Pembayaran · nota {nota.nomor_nota}</h2>
              <div className="text-sm">Total {rupiah(nota.total_snapshot)} · dibayar {rupiah(nota.total_dibayar || 0)} · sisa <b>{rupiah(ringkas.sisa)}</b> · <span data-testid="jersey-status-bayar" className="font-mono text-cyan-300">{nota.status_bayar}</span></div>
              {!batal && ringkas.sisa > 0 && <BayarForm notaId={nota.id} ringkasan={ringkas} rekening={cfg?.rekening} onPaid={load} testid="jersey-bayar" />}
              {nota.revisi?.length > 0 && (
                <div className="text-xs text-slate-400 space-y-0.5" data-testid="jersey-nota-revisi">
                  <div className="font-mono uppercase tracking-wider text-slate-500">Revisi nota</div>
                  {nota.revisi.map((r) => <div key={r.revisi_ke}>Revisi {r.revisi_ke}: {rupiah(r.total_lama)} → {rupiah(r.total_baru)} · {r.alasan} · {r.oleh?.username} · {waktu(r.waktu)}</div>)}
                </div>
              )}
              {nota.pemakaian_bahan?.length > 0 && <div className="text-xs text-slate-500">Bahan tercatat: {nota.pemakaian_bahan.flatMap((g) => g.bahan).map((b) => `${b.kode} ${qty(b.qty_milli)} ${b.satuan || ""}`).join(" · ")}</div>}
            </div>
          )}
          <div className={boxCls}>
            <h2 className={h2Cls}>Riwayat ACC &amp; perubahan</h2>
            <ol className="text-sm space-y-1.5" data-testid="jersey-riwayat">
              {d.acc_riwayat.map((r, i) => (
                <li key={i} className="border-l-2 border-slate-700 pl-3">
                  <span className="font-semibold text-slate-200">{AKSI[r.aksi] || r.aksi}</span>{r.aksi === "UBAH" && ` (revisi ${r.revisi_ke}: ${r.bagian.join(", ")})`}
                  <span className="text-slate-500"> · {r.oleh?.username} · {waktu(r.waktu)}</span>
                  {r.alasan && <div className="text-xs text-amber-200">Alasan: {r.alasan}</div>}
                  {r.aksi === "UBAH" && r.stok?.length > 0 && <div className="text-xs text-slate-400">Stok: {r.stok.map((s) => `${s.kode} ${s.selisih_milli > 0 ? "+" : ""}${qty(s.selisih_milli)}`).join(" · ")}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </main>
    </div>
  );
}
